import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // main.tsx calls this at the top of the module, BEFORE React renders, so an
  // exception here takes the whole app down with a blank page. Error reporting
  // failing must never cost us the app itself.
  try {
    initSentryInner(dsn);
  } catch (e) {
    console.error("[sentry] init failed — continuing without error reporting", e);
  }
}

function initSentryInner(dsn: string) {
  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? "production" : "development",
    // Capture 10% of transactions for performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    // Capture replay only on errors
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Ignore known non-actionable errors
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /^Network Error$/,
      // Unanchored on purpose. Sentry appends the blocked host, so these arrive
      // as "Failed to fetch (www.google.com)" and the old /^Failed to fetch$/
      // never matched them — which is how VA-196 and VA-197 got raised.
      /Failed to fetch/i,
      /Load failed/i,
      // Service-worker update races (post-deploy, multi-tab, etc.). Not actionable —
      // the page still works; the browser just couldn't apply a SW update at that moment.
      /Failed to update a ServiceWorker/i,
      /InvalidStateError.*ServiceWorker/i,
      // In-app browser bridge teardown (VA-218). Android in-app webviews — Facebook,
      // Instagram, LinkedIn — inject their own performance logger, which calls
      // postMessage on a Java bridge object as the page unloads. If the webview has
      // already collected that object the call throws. It is their script, in their
      // browser, during teardown; there is nothing in our page to fix, and the member
      // saw nothing.
      /Java object is gone/i,
      /Error invoking postMessage/i,
    ],
    // Errors thrown by code we don't ship. Browser extensions patch window.fetch,
    // so when an ad blocker kills an analytics request the rejection surfaces as
    // if it were ours (VA-196 / VA-197).
    denyUrls: [
      // Scripts injected by in-app browsers, which we neither ship nor control.
      /^iabjs:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
      /\/gtag\/js/i,
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
      /doubleclick\.net/i,
      /connect\.facebook\.net/i,
      // Microsoft Clarity's session recorder, loaded in index.html (VA-325). Its own script threw
      // "Cannot read properties of null (reading 'sequence')" on /plan, with every frame inside
      // clarity.js. The recorder failing costs the member nothing, and nothing in our code can fix it.
      /clarity\.ms/i,
      /\/clarity\.js/i,
    ],
    beforeSend(event) {
      // denyUrls only inspects the culprit frame. An extension that wraps fetch
      // leaves its frames further down the stack, so drop anything whose trace
      // touches extension code at all — we can never act on those.
      const frames = event.exception?.values?.flatMap(
        (v) => v.stacktrace?.frames ?? []
      ) ?? [];
      const fromExtension = frames.some((f) =>
        /^(chrome|moz|safari(-web)?)-extension:\/\//i.test(f.filename ?? "")
      );
      if (fromExtension) return null;

      // Same reasoning for in-app browser scripts: the culprit frame is often ours
      // (Sentry's own handler wrapper), with the injected script further down the
      // stack — so denyUrls alone does not catch it. VA-218 arrived that way.
      const fromInAppBrowser = frames.some((f) => /^iabjs:\/\//i.test(f.filename ?? ""));
      if (fromInAppBrowser) return null;

      return event;
    },
  });
}

export function setSentryUser(user: { id: string; email?: string } | null) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

export function setSentryTier(tier: string) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setTag("subscription_tier", tier);
}

export function setSentryBrand(brandId: string | null | undefined) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setTag("brand_id", brandId ?? "none");
}

// kept for backwards compat
export function setSentryContext(tier: string, _brandId?: string | null) {
  setSentryTier(tier);
}

export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export { Sentry };
